import { CustomModal, CustomText } from "src/design";
import { LocalizationProvider, PickersDay } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarPicker as MuiCalendar } from "@mui/x-date-pickers";
import { useCallback, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Badge } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";
import { useGetRestaurantLeaves } from "src/data/restaurant/queries/leaves";
import { RestaurantLeave } from "src/business/restaurant/leave";

const lightTheme = createTheme({ palette: { mode: "light" } });

export interface LeaveToggleEvent {
  date: string;
  /** The full persisted leave being deselected, or null when adding a new date. */
  leave: RestaurantLeave | null;
  /** Whether the date was effectively selected before the click. */
  wasSelected: boolean;
}

interface LeaveModalProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  pendingAdds: string[];
  /** Dates (YYYY-MM-DD) that are pending deletion — used to compute effective selection. */
  pendingDeleteDates: string[];
  onToggle: (event: LeaveToggleEvent) => void;
}

const CustomPickersDay = styled(PickersDay)(({ theme, selected }) => ({
  ...(selected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover, &:focus": {
      backgroundColor: theme.palette.primary.main,
    },
  }),
}));

const initialValue = dayjs();
const today = dayjs().startOf("day");

function ServerDay({
  day,
  selected,
  disabled,
  onClick,
}: {
  day: Dayjs;
  selected: boolean;
  disabled: boolean;
  onClick: (day: Dayjs) => void;
}) {
  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      badgeContent={selected ? "❗" : undefined}
    >
      <CustomPickersDay
        day={day}
        onDaySelect={() => !disabled && onClick(day)}
        outsideCurrentMonth={false}
        selected={selected}
        disabled={disabled}
      />
    </Badge>
  );
}

interface ModalContentProps {
  restaurantId: string;
  pendingAdds: string[];
  pendingDeleteDates: string[];
  onToggle: (event: LeaveToggleEvent) => void;
}

const ModalContent = ({
  restaurantId,
  pendingAdds,
  pendingDeleteDates,
  onToggle,
}: ModalContentProps) => {
  const [startOfMonth, setStartOfMonth] = useState(initialValue.startOf("month"));

  const { data: restaurantLeaves } = useGetRestaurantLeaves(
    restaurantId,
    startOfMonth.format("YYYY-MM-DD"),
    startOfMonth.endOf("month").format("YYYY-MM-DD"),
  );

  console.log('pendingDeleteDates: ', pendingDeleteDates);
  console.log('pendingAdds: ', pendingAdds);

  /**
   * Computes the effective selection state for a given day, merging
   * the persisted API data with the in-progress pending changes.
   *
   * API leaves carry ISO timestamps (e.g. "2026-05-26T23:00:00Z"), so we
   * parse them with dayjs and compare only the YYYY-MM-DD portion against
   * the calendar day.  Pending adds/deletes are always stored as YYYY-MM-DD
   * (the creation format), so they use the same `date` string directly.
   */
  const getEffectiveState = useCallback(
    (day: Dayjs): { selected: boolean; leave: RestaurantLeave | null } => {
      const date = day.format("YYYY-MM-DD");
      const apiLeave = (restaurantLeaves ?? []).find(
        (l) => dayjs(l.date).format("YYYY-MM-DD") === date,
      );
      const isPendingDelete = pendingDeleteDates.includes(date);
      const isPendingAdd = pendingAdds.includes(date);

      if (apiLeave && !isPendingDelete) {
        return { selected: true, leave: apiLeave };
      }
      if (isPendingAdd) {
        return { selected: true, leave: null };
      }
      return { selected: false, leave: apiLeave ?? null };
    },
    [restaurantLeaves, pendingDeleteDates, pendingAdds],
  );

  const onDateSelected = useCallback(
    (day: Dayjs) => {
      const date = day.format("YYYY-MM-DD");
      const { selected, leave } = getEffectiveState(day);
      onToggle({ date, leave, wasSelected: selected });
    },
    [getEffectiveState, onToggle],
  );

  const renderDays = useCallback(
    (day: Dayjs) => {
      const { selected } = getEffectiveState(day);
      const isPast = day.isBefore(today);
      return <ServerDay day={day} selected={selected} disabled={isPast} onClick={onDateSelected} />;
    },
    [getEffectiveState, onDateSelected],
  );

  return (
    <MuiCalendar
      date={startOfMonth}
      onChange={() => null}
      renderDay={renderDays}
      minDate={today}
      onMonthChange={(day) => setStartOfMonth(day.startOf("month"))}
      onYearChange={(year) => setStartOfMonth(year.startOf("year"))}
    />
  );
};

const LeaveModal = ({
  open,
  onClose,
  restaurantId,
  pendingAdds,
  pendingDeleteDates,
  onToggle,
}: LeaveModalProps) => {
  const { t } = useTranslation();

  return (
    <CustomModal show={open} setShow={onClose}>
      <CustomText
        label={t("restaurant.restaurantInfoPage.leaveDays.header")}
        type="header"
      />
      <CustomText
        label={t("restaurant.restaurantInfoPage.leaveDays.subheader")}
        type="subheader"
      />

      <ThemeProvider theme={lightTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ModalContent
            restaurantId={restaurantId}
            pendingAdds={pendingAdds}
            pendingDeleteDates={pendingDeleteDates}
            onToggle={onToggle}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </CustomModal>
  );
};

export default LeaveModal;
